import { Button } from "@/components/ui/baseUi/button"
import { Text } from "@/components/ui/baseUi/text"
import { EmailIcon } from "@images/svgIcons/emailIcon"
import { PhoneIcon } from "@images/svgIcons/phoneIcon"
import { type BoxFormPageProps } from "@modules/box/components/boxCreationForm/boxFormFirstPage"
import { BoxFormHeader } from "@modules/box/components/boxCreationForm/boxFormHeader"
import { LocationBox } from "@modules/box/components/locationBox"
import { word } from "@modules/core/utils/i18n"
import { formatPhoneNumber } from "@modules/utils/numberUtils"
import { type CountryCode } from "libphonenumber-js"
import { ArrowLeft, ArrowRight, Loader } from "lucide-react"
import { useMemo } from "react"
import styled from "styled-components"

export const BoxFormResumePage = ({ handleSubmit, onPrevious, formik, isSubmitting }: BoxFormPageProps) => {
  const urlLogo = useMemo(() => {
    return formik.values.logo?.file
      ? URL.createObjectURL(formik.values.logo.file)
      : (formik.values.logo?.url ?? undefined)
  }, [formik.values.logo])
  const urlCover = useMemo(() => {
    return formik.values.cover?.file
      ? URL.createObjectURL(formik.values.cover.file)
      : (formik.values.cover?.url ?? undefined)
  }, [formik.values.cover])

  return (
    <>
      <BoxFormHeader title={word("box.creation.page.resume.title")} classname="mb-5" />
      <div style={{ maxWidth: "36rem" }} className="flex w-full flex-col gap-8">
        <div className="flex h-[14rem] rounded-2xl bg-neutral-50">
          <div className="m-auto flex flex-col items-center">
            <div style={{ width: "100px", height: "100px" }}>
              <img src={urlLogo} className="h-full w-full rounded-full" style={{ objectFit: "cover" }} />
            </div>
            <Text fontStyle="HS" className="mt-2">
              {formik.values.name}
            </Text>
            <Text fontStyle="LL">
              {formik.values.city} <Text fontStyle="BM">{formik.values.postalCode}</Text>
            </Text>
          </div>
          <BoxFormResumeHeader className="rounded-t-2xl">
            <img src={urlCover} style={{ objectFit: "cover" }} className="h-full w-full" />
          </BoxFormResumeHeader>
        </div>
        <div>
          <Text fontStyle="TS">{word("box.form.contact.title")}</Text>
          <div className="mt-2 mb-2 flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-neutral-100">
              <EmailIcon color="currentColor" width={15} height={15} />
            </div>
            <Text fontStyle="BM">{formik.values.email}</Text>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-neutral-100">
              <PhoneIcon color="currentColor" />
            </div>
            <Text fontStyle="BM">
              {formatPhoneNumber(formik.values.phoneNumber, formik.values.phoneNumberCountryCode as CountryCode)}
            </Text>
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <Text fontStyle="TS">{word("box.where.address.title")}</Text>
          <LocationBox
            box={{
              address: {
                address: formik.values.address,
                additionalInformation: formik.values.additionalAddress,
                postalCode: formik.values.postalCode,
                city: formik.values.city,
                country: formik.values.country,
              },
              name: formik.values.name,
            }}
            bgColor={{ icon: "#242424" }}
            className="bg-neutral-50"
          />
        </div>
        <Text
          fontStyle="BM"
          className="text-neutral-700"
          style={{ whiteSpace: "break-spaces", overflowWrap: "break-word" }}
        >
          {formik.values.description}
        </Text>
        <div className="flex flex-col gap-2">
          <Text fontStyle="TS">{word("box.creation.page.legal.files")}</Text>
          <Text fontStyle="LS" className="text-neutral-600">
            {word("box.creation.page.legal.label").toUpperCase()}
          </Text>
          <div>
            <Text fontStyle="TS">{formik.values.siren}</Text>
          </div>
        </div>
        <div className="flex justify-center gap-2">
          <Button variant="outline" size="lg" className="mb-5" onClick={onPrevious}>
            <ArrowLeft size={18} color="currentColor" />
            {word("global.previous")}
          </Button>
          <Button variant="primary" size="lg" className="mb-5" onClick={handleSubmit} disabled={isSubmitting}>
            {word("box.creation.saveAndCreate")}
            {isSubmitting ? <Loader className="animate-spin" /> : <ArrowRight size={18} color="currentColor" />}
          </Button>
        </div>
      </div>
    </>
  )
}

const BoxFormResumeHeader = styled.div`
  background-color: var(--primary-700);
  overflow: hidden;
  clip-path: polygon(32% 0%, 100% 0%, 100% 100%, 0% 100%);
  border-bottom-right-radius: 1rem;
  width: 50%;
`
